import { useState } from 'react';
import { documentService } from '@/services/documents/documentService';
import { Document } from '@/services/db/types';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { saveFileToAppDirectory, getFileFromAppDirectory } from '@/services/db/adapter/desktopPathUtils';
import { toast } from 'sonner';

export const useDocumentManagement = (studentId: number) => {
  const [loading, setLoading] = useState(false);

  const trackDocumentAction = async (
    documentId: string,
    action: 'upload' | 'verify' | 'replace' | 'delete',
    details: Partial<Document>
  ) => {
    setLoading(true);
    try {
      // Only attempt to track if we have a valid student ID
      if (studentId) {
        await documentService.trackDocumentHistory(studentId, documentId, action, details);
      } else {
        console.log('Document action tracked locally only - no student ID provided');
      }
    } catch (error) {
      console.error('Error tracking document action:', error);
      // Don't show error toast here as it might confuse users during normal operations
    } finally {
      setLoading(false);
    }
  };

  const verifyDocument = async (
    documentId: string,
    status: 'pending' | 'verified' | 'rejected',
    verifiedBy: string,
    comments?: string
  ) => {
    setLoading(true);
    try {
      await documentService.updateVerificationStatus(studentId, documentId, status, verifiedBy, comments);
    } finally {
      setLoading(false);
    }
  };

  const checkExpirations = async () => {
    setLoading(true);
    try {
      await documentService.checkDocumentExpirations(studentId);
    } finally {
      setLoading(false);
    }
  };

  const replaceDocument = async (
    oldDocumentId: string,
    newDocument: Partial<Document>
  ) => {
    setLoading(true);
    try {
      await documentService.replaceDocument(studentId, oldDocumentId, newDocument);
    } finally {
      setLoading(false);
    }
  };
  
  // New functions for desktop environment

  const saveLocalDocument = async (fileData: string, fileName: string): Promise<string> => {
    setLoading(true);
    try {
      if (Capacitor.isNativePlatform()) {
        // Use Capacitor to save to local filesystem
        return await saveFileToAppDirectory(fileName, fileData);
      } else {
        // Fallback for web - convert to blob and save using URL
        const blob = dataURItoBlob(fileData);
        const url = URL.createObjectURL(blob);
        return url;
      }
    } catch (error) {
      console.error('Error saving document locally:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  const getLocalDocument = async (filePath: string): Promise<string> => {
    setLoading(true);
    try {
      if (Capacitor.isNativePlatform()) {
        // Use Capacitor to read from local filesystem
        return await getFileFromAppDirectory(filePath);
      } else {
        // For web, we assume the filePath is already a URL
        return filePath;
      }
    } catch (error) {
      console.error('Error retrieving local document:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };
  
  // Helper function to convert data URI to Blob
  const dataURItoBlob = (dataURI: string): Blob => {
    // Split the dataURI to get the data part
    const byteString = dataURI.split(',')[1];
    // Get the MIME type
    const mimeString = dataURI.split(',')[0].split(':')[1].split(';')[0];
    
    // Convert base64 to raw binary data
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    
    return new Blob([ab], { type: mimeString });
  };

  return {
    loading,
    trackDocumentAction,
    verifyDocument,
    checkExpirations,
    replaceDocument,
    saveLocalDocument,
    getLocalDocument
  };
};
